import React, { useState, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import Turnstile from './Turnstile';
import '../styles/Auth.css';

function Signup() {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [turnstileToken, setTurnstileToken] = useState(null);
  const turnstileRef = useRef(null);
  const { signup } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    // Validate password requirements (must match backend)
    const errors = [];
    if (password.length < 12) {
      errors.push('12+ characters');
    }
    if (!/[A-Z]/.test(password)) {
      errors.push('uppercase letter');
    }
    if (!/[a-z]/.test(password)) {
      errors.push('lowercase letter');
    }
    if (!/\d/.test(password)) {
      errors.push('number');
    }
    if (!/[!@#$%^&*(),.?":{}|<>\-_=+[\]\\;'`~]/.test(password)) {
      errors.push('special character');
    }
    
    if (errors.length > 0) {
      setError(`Password needs: ${errors.join(', ')}`);
      // Don't consume Turnstile token for client-side validation failures
      return;
    }

    setLoading(true);

    try {
      await signup(email, password, name || undefined, turnstileToken);
      navigate('/chat');
    } catch (err) {
      setError(err.message || 'Signup failed. Please try again.');
    } finally {
      setLoading(false);
      // Always reset Turnstile after submission attempt (success or failure)
      // Tokens are single-use — must get a fresh one for retry
      turnstileRef.current?.reset();
    }
  };

  return (
    <div className="auth-page">
      <div className="auth-container">
        <div className="auth-header">
          <h1 className="auth-title">Create your account</h1>
          <p className="auth-subtitle">Start building in minutes</p>
        </div>

        <form onSubmit={handleSubmit} className="auth-form">
          {error && (
            <div className="auth-error">
              {error}
            </div>
          )}

          <div className="form-group">
            <label htmlFor="name" className="form-label">
              Name <span className="form-optional">(optional)</span>
            </label>
            <input
              id="name"
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="form-input"
              placeholder="What should we call you?"
              autoFocus
            />
          </div>

          <div className="form-group">
            <label htmlFor="email" className="form-label">Email</label>
            <input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="form-input"
              placeholder="you@example.com"
              required
            />
          </div>

          <div className="form-group">
            <label htmlFor="password" className="form-label">
              Password <span className="form-hint">(12+ chars, upper/lower/number/special)</span>
            </label>
            <input
              id="password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="form-input"
              placeholder="Strong password required"
              required
              minLength={12}
            />
          </div>

          <Turnstile ref={turnstileRef} onVerify={setTurnstileToken} />

          <button 
            type="submit" 
            className="btn btn-primary btn-full"
            disabled={loading || !turnstileToken}
          >
            {loading ? 'Creating account...' : 'Create Account'}
          </button>
        </form>

        <p className="auth-switch">
          Already have an account?{' '}
          <Link to="/login" className="auth-link">Sign in</Link>
        </p>
      </div>
    </div>
  );
}

export default Signup;
